<script setup lang="ts">
/**
 * Who works here, which tills exist, and what the venue believes about time.
 *
 * The roster is names and roles and NOTHING ELSE — no verifier, no secret. A
 * staff PIN verifier is minted per device and is never listed, which is what
 * makes a stolen back-office session not also a stolen till.
 *
 * The trading-day start is here because it explains more of this product than
 * anything else on the screen: it is why Friday night's takings are Friday's.
 */

import type { LocaleName } from "../locales";
import { LOCALES, LOCALE_NAMES } from "../locales";

const props = defineProps<{
  venue: {
    name: string;
    timezone: string;
    businessDayStartMinutes: number;
    fiscalRegistrationId: string | null;
  } | null;
  devices: readonly { deviceId: string; name: string; fiscalTillId: string | null }[];
  staff: readonly { staffId: string; displayName: string; roleId: string }[];
  locale: LocaleName;
  t: (key: string) => string;
  /**
   * WHICH HALF OF THIS SCREEN TO SHOW.
   *
   * The nav has both "Lidé" and "Nastavení" and this component was the `v-else`
   * catch-all for both, so the two items rendered exactly the same page — the
   * roster sitting at the bottom of a wall of venue configuration. Clicking
   * "People" and getting timezone settings is the kind of thing a customer
   * notices in the first minute and never quite unsees.
   *
   * Split by prop rather than into two files: it is one data shape, one fetch
   * and one layout, and two components would be two places to keep a table in
   * step for no gain.
   */
  section: "venue" | "staff";
}>();

defineEmits<{ locale: [value: LocaleName] }>();

const roleName = (roleId: string): string => {
  const key = `role.${roleId}`;
  const name = props.t(key);
  return name === key ? roleId : name;
};

const clockOf = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
</script>

<template>
  <section class="gf-columns">
    <div v-if="section === 'venue'" class="gf-panel">
      <h2 class="gf-panel__title">{{ t("settings.venue") }}</h2>
      <dl v-if="venue" class="gf-terms">
        <dt>{{ t("settings.venue") }}</dt>
        <dd>{{ venue.name }}</dd>
        <dt>{{ t("settings.timezone") }}</dt>
        <dd>{{ venue.timezone }}</dd>
        <dt>{{ t("settings.dayStart") }}</dt>
        <dd>{{ clockOf(venue.businessDayStartMinutes) }}</dd>
        <dt>{{ t("settings.fiscalId") }}</dt>
        <dd>{{ venue.fiscalRegistrationId ?? t("common.none") }}</dd>
      </dl>


      <h2 class="gf-panel__title">{{ t("settings.language") }}</h2>
      <div class="gf-switch" role="group" :aria-label="t('settings.language')">
        <button
          v-for="name in LOCALES"
          :key="name"
          type="button"
          class="gf-switch__option"
          :aria-pressed="locale === name"
          @click="$emit('locale', name)"
        >
          {{ LOCALE_NAMES[name] }}
        </button>
      </div>
    </div>

    <div v-if="section === 'staff'" class="gf-panel">
      <h2 class="gf-panel__title">{{ t("staff.heading") }}</h2>
      <table class="gf-table">
        <thead>
          <tr>
            <th scope="col">{{ t("staff.name") }}</th>
            <th scope="col">{{ t("staff.role") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="person in staff" :key="person.staffId">
            <th scope="row">{{ person.displayName }}</th>
            <!--
              Translated, falling back to the identifier. `t` returns the KEY
              when it has no message, which is the documented behaviour and
              exactly the signal needed: a role nobody has named shows as itself
              — ugly and true, rather than blank.
            -->
            <td class="gf-muted">{{ roleName(person.roleId) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<style scoped>
/* MyMonday's segmented control: a sunken track, the chosen option lifted out. */
.gf-switch {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 3px;
  background: var(--gf-color-surface-sunken);
  border-radius: var(--gf-radius-md);
  box-shadow: inset 0 0 0 var(--gf-border-thin) var(--gf-color-border-subtle);
}

.gf-switch__option {
  min-height: 32px;
  padding: 0 var(--gf-space-3);
  font: inherit;
  font-size: var(--gf-text-sm);
  font-weight: var(--gf-weight-medium);
  border: 0;
  border-radius: var(--gf-radius-sm);
  background: transparent;
  color: var(--gf-color-text-muted);
  cursor: pointer;
  transition:
    background-color var(--gf-motion-fast) var(--gf-ease-out),
    color var(--gf-motion-fast) var(--gf-ease-out);
}

.gf-switch__option:hover {
  color: var(--gf-color-text);
}

/*
  The chosen language is carried by `aria-pressed` — so the state is announced,
  not merely drawn. It was a solid ink fill; now the lifted segment, neutral.
*/
.gf-switch__option[aria-pressed="true"] {
  color: var(--gf-color-text);
  background: var(--gf-color-surface);
  box-shadow:
    0 0 0 var(--gf-border-thin) var(--gf-color-border-subtle),
    var(--gf-shadow-sm);
}

.gf-switch__option:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}
</style>
